import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import type { ModelName } from "../types";
import { TellescopeModelFromName } from "../mappings";

/**
 * A single model item found in the cache, paired with the timestamp of when
 * the query that contained it was last updated.
 */
interface ResolvedCacheEntry<Model extends ModelName> {
  data: TellescopeModelFromName<Model>;
  dataUpdatedAt: number;
}

/**
 * Searches all cached `getSome` (infinite) and `getByIds` queries for a single
 * model item by ID, returning the copy from the most recently updated query.
 *
 * Useful for populating a `getOne` cache entry from data that has already been
 * fetched as part of a collection, avoiding a redundant network request.
 *
 * @param queryClient - The TanStack Query client holding the cache to search.
 * @param model - The Tellescope model name (e.g. `"endusers"`).
 * @param id - The ID of the item to look up.
 * @returns The freshest matching cache entry, or `undefined` if the item is not
 *   found in any collection cache.
 */
export const resolveFromCollectionCaches = <Model extends ModelName>({
  queryClient,
  model,
  id,
}: {
  queryClient: QueryClient;
  model: Model;
  id: string;
}): ResolvedCacheEntry<Model> | undefined => {
  let best: ResolvedCacheEntry<Model> | undefined;

  // 1. Check getSome infinite queries
  const getSomeQueries = queryClient.getQueriesData<InfiniteData<Array<TellescopeModelFromName<Model>>>>({
    queryKey: [model, "getSome"],
    exact: false,
  });

  for (const [queryKey, data] of getSomeQueries) {
    if (!data?.pages) continue;
    for (const page of data.pages) {
      if (!Array.isArray(page)) continue;
      const found = page.find((item) =>
        typeof item === "object" && item !== null && "id" in item ? item.id === id : false
      );
      if (found) {
        const state = queryClient.getQueryState(queryKey);
        const dataUpdatedAt = state?.dataUpdatedAt ?? 0;
        if (!best || dataUpdatedAt > best.dataUpdatedAt) {
          best = { data: found, dataUpdatedAt };
        }
      }
    }
  }

  // 2. Check getByIds standard queries
  const getByIdsQueries = queryClient.getQueriesData<{ matches: Array<TellescopeModelFromName<Model>> }>({
    queryKey: [model, "getByIds"],
    exact: false,
  });

  for (const [queryKey, data] of getByIdsQueries) {
    if (!data?.matches) continue;
    const found = data.matches.find((item) =>
      typeof item === "object" && item !== null && "id" in item ? item.id === id : false
    );
    if (found) {
      const state = queryClient.getQueryState(queryKey);
      const dataUpdatedAt = state?.dataUpdatedAt ?? 0;
      if (!best || dataUpdatedAt > best.dataUpdatedAt) {
        best = { data: found, dataUpdatedAt };
      }
    }
  }

  return best;
};
